import React, { useMemo, useState } from 'react';
import axiosInstance from '../api/axiosInstance';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import AuthShell from '../components/public/AuthShell';
import PasswordInput from '../components/public/PasswordInput';
import { passwordStrength } from './register/validators';

const ResetPassword = () => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // token from ?token= or from hash (/#/reset-password?token=...)
  const token = useMemo(() => {
    const q = searchParams.get('token');
    if (q) return q;
    const hash = window.location.hash || '';
    const idx = hash.indexOf('?');
    if (idx >= 0) {
      const qs = new URLSearchParams(hash.slice(idx + 1));
      return qs.get('token');
    }
    return null;
  }, [searchParams]);

  const strength = passwordStrength(newPassword);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) return setError('This reset link is invalid or incomplete. Please request a new one.');
    if (newPassword.length < 8) return setError('Password must be at least 8 characters long.');
    if (newPassword !== confirmPassword) return setError('Passwords do not match.');

    setIsLoading(true);
    try {
      await axiosInstance.post('/User/confirm-reset-password', { token, newPassword });
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reset password.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="This link doesn't work" subtitle="It is missing its security code, or it has been used or has expired." status="error">
        <div className="auth-stack">
          <Link to="/Forgot-Password" className="auth-btn auth-btn--block">
            Request a new link
          </Link>
          <Link to="/login" className="auth-btn auth-btn--ghost auth-btn--block">
            Back to sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  if (done) {
    return (
      <AuthShell title="Password updated" subtitle="You can now sign in with your new password." status="success">
        <div className="auth-stack">
          <Link to="/login" className="auth-btn auth-btn--block">
            Sign in
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Set a new password" subtitle="Choose a password you haven't used before.">
      {error && (
        <div className="auth-alert is-error" role="alert">
          {error}
        </div>
      )}

      <form onSubmit={handleResetPassword} noValidate>
        <div className="auth-field">
          <label htmlFor="rp-new">New password</label>
          <PasswordInput
            id="rp-new"
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              if (error) setError('');
            }}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            disabled={isLoading}
            required
          />
          {newPassword && (
            <div className={`pw-meter s${strength.score}`} aria-live="polite">
              <span className="pw-bars" aria-hidden="true">
                <i />
                <i />
                <i />
                <i />
              </span>
              <span className="pw-label">{strength.label}</span>
            </div>
          )}
        </div>

        <div className="auth-field">
          <label htmlFor="rp-confirm">Confirm new password</label>
          <PasswordInput
            id="rp-confirm"
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              if (error) setError('');
            }}
            placeholder="Re-enter the new password"
            autoComplete="new-password"
            disabled={isLoading}
            required
          />
        </div>

        <button type="submit" className="auth-btn auth-btn--block" disabled={isLoading}>
          {isLoading ? 'Updating...' : 'Update password'}
        </button>
      </form>

      <p className="auth-links">
        <Link to="/login">Back to sign in</Link>
      </p>
    </AuthShell>
  );
};

export default ResetPassword;
